import React, { useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCompressArrowsAlt,
  faExpandArrowsAlt,
  faExternalLinkAlt,
  faEye,
  faSpinner,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";
import TransactionDetailsView from "./TransactionDetailsView";

const TransactionDetailsDialog = ({
  isOpen,
  selectedTransaction,
  data,
  loading,
  onClose,
}) => {
  const navigate = useNavigate();
  const [isMaximized, setIsMaximized] = useState(false);

  if (!isOpen || !selectedTransaction) return null;

  const details = data || selectedTransaction || {};
  const transNo = details.trans_no || selectedTransaction.trans_no;

  const handleViewFullPage = () => {
    if (!transNo) return;
    navigate(`/transactions/${encodeURIComponent(String(transNo))}/details`);
  };

  return createPortal(
    <div
      className="fixed inset-0 z-50 overflow-y-auto"
      aria-labelledby="modal-title"
      role="dialog"
      aria-modal="true"
    >
      <div className="flex min-h-full items-center justify-center p-4 text-center">
        <button
          type="button"
          aria-label="Close transaction details"
          className="fixed inset-0 bg-slate-900/60"
          onClick={onClose}
        />
        <div
          className={`relative flex w-full transform flex-col overflow-hidden rounded-lg bg-slate-100 text-left shadow-xl transition-all ${
            isMaximized
              ? "h-[96vh] max-w-[98vw]"
              : "max-h-[92vh] max-w-6xl"
          }`}
        >
          <div className="flex shrink-0 flex-col gap-3 border-b border-slate-200 bg-white px-4 py-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex min-w-0 items-center gap-3">
              <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-indigo-50 text-indigo-600">
                <FontAwesomeIcon icon={faEye} />
              </span>
              <div className="min-w-0">
                <h3
                  id="modal-title"
                  className="truncate text-base font-bold text-slate-950"
                >
                  Transaction Details
                </h3>
                <p className="truncate text-sm text-slate-500">
                  {transNo || "-"}
                </p>
              </div>
            </div>
            <div className="flex items-center justify-end gap-2">
              <button
                type="button"
                className="inline-flex items-center justify-center rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                onClick={handleViewFullPage}
              >
                <FontAwesomeIcon icon={faExternalLinkAlt} className="mr-2" />
                View Full Page
              </button>
              <button
                type="button"
                className="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
                onClick={() => setIsMaximized((current) => !current)}
                aria-label={isMaximized ? "Restore modal" : "Maximize modal"}
              >
                <FontAwesomeIcon
                  icon={isMaximized ? faCompressArrowsAlt : faExpandArrowsAlt}
                />
              </button>
              <button
                type="button"
                className="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
                onClick={onClose}
                aria-label="Close modal"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
          </div>

          <div
            className={`min-h-0 flex-1 overflow-y-auto p-4 ${
              isMaximized ? "max-h-[calc(96vh-76px)]" : "max-h-[78vh]"
            }`}
          >
            {loading ? (
              <div className="flex h-64 items-center justify-center rounded-lg border border-slate-200 bg-white">
                <FontAwesomeIcon
                  icon={faSpinner}
                  className="mr-3 animate-spin text-2xl text-indigo-600"
                />
                <span className="text-sm font-semibold text-slate-700">
                  Loading transaction details...
                </span>
              </div>
            ) : (
              <TransactionDetailsView data={details} compact />
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default TransactionDetailsDialog;
